import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { NavigationItem, SidebarProps } from '@/types/components';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { useStnkBadge } from './useStnkBadge';

// 2 Wheel navigation, grouped by workflow stage instead of a flat list.
// Home & Dashboard stay pinned above the groups (built in-component behind
// feature flags). Group ids double as the collapse-state keys.
type TwoWheelGroupId = 'dailyOps' | 'setup' | 'rent' | 'compliance' | 'finance';

interface NavigationGroup {
  id: TwoWheelGroupId;
  label: string;
  /** Tailwind bg-* class for the small leading dot on the group header. */
  dotClass: string;
  items: NavigationItem[];
}

const TWO_WHEEL_GROUPS: NavigationGroup[] = [
  {
    id: 'setup',
    label: 'Setup & Masterdata',
    dotClass: 'bg-gray-400',
    items: [
      {
        id: 'oem',
        title: 'OEMs',
        router: '/oems',
        icon: 'ri-building-2-line',
      },
      {
        id: 'models',
        title: 'Models',
        router: '/models',
        icon: 'ri-motorbike-line',
      },
      {
        id: 'purchase-orders',
        title: 'Purchase Order',
        router: '/purchase-orders',
        icon: 'ri-shopping-cart-2-line',
      },
    ],
  },
  {
    id: 'rent',
    label: 'Rent',
    dotClass: 'bg-blue-500',
    items: [
      {
        id: 'rent-dashboard',
        title: 'Dashboard',
        router: '/rent-dashboard',
        icon: 'ri-dashboard-2-line',
      },
      {
        id: 'package',
        title: 'Package',
        router: '/packages',
        icon: 'ri-vip-diamond-line',
      },
      {
        id: 'rent-activity',
        title: 'Rent Activity',
        router: '/rent-activity',
        icon: 'ri-article-line',
      },
      {
        id: 'rent-payments',
        title: 'Pembayaran',
        router: '/rent-payments',
        icon: 'ri-money-dollar-circle-line',
      },
      {
        id: 'outstanding',
        title: 'Outstanding',
        router: '/outstandings',
        icon: 'ri-hand-coin-line',
      },
      {
        id: 'rent-tracking',
        title: 'Tracking',
        router: '/rent-tracking',
        icon: 'ri-map-pin-line',
      },
    ],
  },
  {
    id: 'dailyOps',
    label: 'Daily Operations',
    dotClass: 'bg-emerald-500',
    items: [
      {
        id: 'vehicles',
        title: 'Vehicles',
        router: '/vehicles',
        icon: 'ri-e-bike-line',
      },
      {
        id: 'handovers',
        title: 'Handovers',
        router: '/handovers',
        icon: 'ri-exchange-line',
      },
      {
        id: 'monitoring',
        title: 'Monitoring',
        router: '/monitoring',
        icon: 'ri-pulse-line',
      },
      {
        id: 'incidents',
        title: 'Incidents',
        router: '/incidents',
        icon: 'ri-indeterminate-circle-line',
      },
      {
        id: 'maintenances',
        title: 'Maintenances',
        router: '/maintenances',
        icon: 'ri-settings-5-line',
      },
      {
        id: 'repossessions',
        title: 'Repossessions',
        router: '/repossessions',
        icon: 'ri-alert-line',
      },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    dotClass: 'bg-orange-500',
    items: [
      {
        id: 'stnk',
        title: 'STNK Management',
        router: '/2w/stnk',
        icon: 'ri-file-shield-2-line',
      },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    dotClass: 'bg-purple-500',
    items: [
      {
        id: 'invoice',
        title: 'Invoice',
        router: '/2w/invoice',
        icon: 'ri-file-list-3-line',
      },
    ],
  },
];

const bottomMenuItems: NavigationItem[] = [
  {
    id: 'settings',
    title: 'Settings',
    router: '/settings',
    icon: 'ri-settings-4-line',
  },
  {
    id: 'support',
    title: 'Support',
    router: '/support',
    icon: 'ri-question-line',
  },
];

const renderMenuItem = (
  item: NavigationItem,
  isActive: boolean,
  isCollapsed: boolean,
  extraClasses: string = ''
) => {
  const baseClasses = isCollapsed
    ? `w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${extraClasses}`
    : `group flex items-center min-h-[44px] md:min-h-0 px-3 py-2 text-sm font-sans font-medium rounded-lg transition-colors ${extraClasses}`;

  const activeClasses = isActive
    ? isCollapsed
      ? 'bg-purple-100 text-primary'
      : 'bg-purple-50 text-primary border-r-2 border-primary'
    : isCollapsed
      ? 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900';

  const disabledClasses = item.disabled
    ? 'opacity-50 cursor-not-allowed pointer-events-none'
    : '';

  return (
    <Link
      key={item.id}
      to={item.disabled ? '#' : item.router}
      className={`${baseClasses} ${activeClasses} ${disabledClasses}`.trim()}
      title={isCollapsed ? item.title : undefined}
      onClick={(e) => item.disabled && e.preventDefault()}
    >
      <i
        className={`${item.icon} text-[20px] ${
          isCollapsed
            ? ''
            : isActive
              ? 'text-primary'
              : 'text-gray-400 group-hover:text-gray-500'
        }`}
      ></i>
      {!isCollapsed && (
        <>
          <span className="ml-3">{item.title}</span>
          {item.badge && (
            <span className="ml-auto mr-2 px-2 py-0.5 text-xs font-medium bg-purple-100 text-primary rounded-full">
              {item.badge}
            </span>
          )}
          <i className="ri-arrow-right-s-line text-[16px] ml-auto"></i>
        </>
      )}
      {isCollapsed && item.badge && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
          {typeof item.badge === 'number' && item.badge > 9 ? '9+' : item.badge}
        </span>
      )}
    </Link>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
  currentPath = '/oems',
  variant = 'inline',
  onRequestClose,
}) => {
  // Inside the phone drawer the sidebar fills it and never collapses; the fold
  // toggle becomes a close button.
  const inDrawer = variant === 'drawer';
  const { user, signOut } = useAuth();
  const stnkBadge = useStnkBadge();

  const showDashboard = useFeatureFlag('web_fms_show_dashboard', 'boolean');
  const showHome = useFeatureFlag('web_fms_show_home', 'boolean');

  // Home & Dashboard stay pinned above the workflow groups (same as before).
  const pinnedTwoWheelItems: NavigationItem[] = [
    ...(showHome
      ? [
          {
            id: 'home',
            title: 'Home',
            router: '/home',
            icon: 'ri-home-line',
          },
        ]
      : []),
    ...(showDashboard
      ? [
          {
            id: 'dashboard',
            title: 'Dashboard',
            router: '/dashboard',
            icon: 'ri-layout-grid-line',
          },
        ]
      : []),
  ];

  // Inject the live STNK badge into its item within the Compliance group.
  const twoWheelGroups: NavigationGroup[] = TWO_WHEEL_GROUPS.map((group) => ({
    ...group,
    items: group.items.map((item) =>
      item.id === 'stnk' && stnkBadge > 0 ? { ...item, badge: stnkBadge } : item
    ),
  }));

  // Flat, ordered list used only by the collapsed (icon-only) rail, where
  // group headers don't apply.
  const collapsedTwoWheelItems: NavigationItem[] = [
    ...pinnedTwoWheelItems,
    ...twoWheelGroups.flatMap((group) => group.items),
  ];

  const navigation4WheelItems: NavigationItem[] = [
    ...(showHome
      ? [
          {
            id: '4w-home',
            title: 'Home',
            router: '/4w/home',
            icon: 'ri-home-line',
          },
        ]
      : []),
  ];

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {}
  };

  const [expandedSections, setExpandedSections] = useState({
    twoWheel: true,
    fourWheel: true,
    // 2 Wheel workflow sub-groups (collapsible independently, and all hidden
    // together when the parent 2 Wheel section is collapsed).
    dailyOps: true,
    setup: true,
    rent: true,
    compliance: true,
    finance: true,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div
      className={`bg-white border-r border-gray-200 flex flex-col h-full transition-all duration-300 ${
        isCollapsed ? 'w-16' : inDrawer ? 'w-full' : 'w-64'
      }`}
    >
      <div
        className={`shrink-0 flex items-center ${
          isCollapsed ? 'justify-center p-4' : 'justify-between p-4'
        } border-b border-gray-200`}
      >
        <div
          className={`flex ${
            isCollapsed
              ? 'flex-col items-center space-y-2'
              : 'flex-row items-center space-x-3'
          }`}
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center">
            <img
              src="/images/dash-electric.png"
              alt="Dash Logo"
              className="w-6 h-6"
            />
          </div>
          {isCollapsed && (
            <button
              onClick={onToggleCollapse}
              aria-label="Tampilkan sidebar"
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <i className="ri-sidebar-unfold-line text-[20px] text-gray-600"></i>
            </button>
          )}
          {!isCollapsed && (
            <div>
              <h1 className="text-lg font-sans font-semibold text-gray-900">
                Dash Electric Fleet
              </h1>
              <p className="text-xs font-sans text-gray-500">
                Fleet Management
              </p>
            </div>
          )}
        </div>
        {!isCollapsed && inDrawer && (
          <button
            type="button"
            onClick={onRequestClose}
            aria-label="Tutup menu"
            className="h-11 w-11 -mr-2 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-[22px] text-gray-600"></i>
          </button>
        )}
        {!isCollapsed && !inDrawer && (
          <button
            onClick={onToggleCollapse}
            aria-label="Sembunyikan sidebar"
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="ri-sidebar-fold-line text-[20px] text-gray-600"></i>
          </button>
        )}
      </div>

      <nav
        aria-label="Navigasi utama"
        className={`flex-1 min-h-0 overflow-y-auto flex flex-col ${
          isCollapsed ? 'items-center space-y-2 py-2' : 'px-3 py-4'
        }`}
      >
        {isCollapsed ? (
          <>
            <span className="text-sm font-sans font-medium text-gray-400 mb-2 mt-2">
              2W
            </span>
            {collapsedTwoWheelItems.map((item) => {
              const isActive = currentPath === item.router;
              return (
                <div key={item.id} className="relative">
                  {renderMenuItem(item, isActive, true)}
                </div>
              );
            })}

            <span className="text-sm font-sans font-medium text-gray-400 mb-2 mt-2 pt-4">
              4W
            </span>
            {navigation4WheelItems.map((item) => {
              const isActive = currentPath === item.router;
              return (
                <div key={item.id} className="relative">
                  {renderMenuItem(item, isActive, true)}
                </div>
              );
            })}
          </>
        ) : (
          <div className="space-y-1 w-full">
            <div className="space-y-1">
              <button
                onClick={() => toggleSection('twoWheel')}
                className="pb-2 w-full flex items-center justify-between text-sm font-sans font-medium text-gray-500 tracking-wide mb-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
              >
                <span>2 Wheel</span>
                <svg
                  className={`w-3 h-3 transition-transform duration-300 ease-in-out ${
                    expandedSections.twoWheel ? 'rotate-180' : 'rotate-0'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  expandedSections.twoWheel
                    ? 'max-h-screen opacity-100'
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="space-y-1">
                  {pinnedTwoWheelItems.map((item) => {
                    const isActive = currentPath === item.router;
                    return renderMenuItem(item, isActive, false);
                  })}

                  {twoWheelGroups.map((group) => (
                    <div key={group.id} className="pt-1">
                      <button
                        onClick={() => toggleSection(group.id)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs font-sans font-semibold uppercase tracking-wide text-gray-400 hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${group.dotClass}`}
                        ></span>
                        <span>{group.label}</span>
                        <svg
                          className={`ml-auto w-3 h-3 transition-transform duration-300 ease-in-out ${
                            expandedSections[group.id]
                              ? 'rotate-180'
                              : 'rotate-0'
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          expandedSections[group.id]
                            ? 'max-h-96 opacity-100'
                            : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="space-y-1 pt-1">
                          {group.items.map((item) => {
                            const isActive = currentPath === item.router;
                            return renderMenuItem(item, isActive, false);
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <button
                onClick={() => toggleSection('fourWheel')}
                className="pt-3 w-full flex items-center justify-between text-sm font-sans font-medium text-gray-500 tracking-wide mb-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
              >
                <span>4 Wheel</span>
                <svg
                  className={`w-3 h-3 transition-transform duration-300 ease-in-out ${
                    expandedSections.fourWheel ? 'rotate-180' : 'rotate-0'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  expandedSections.fourWheel
                    ? 'max-h-screen opacity-100'
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="space-y-1">
                  {navigation4WheelItems.map((item) => {
                    const isActive = currentPath === item.router;
                    return renderMenuItem(item, isActive, false);
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {isCollapsed ? (
        <div className="shrink-0 p-4 flex flex-col items-center space-y-4">
          {bottomMenuItems.map((item) => {
            const isActive = currentPath === item.router;
            return (
              <div key={item.id} className="relative">
                {renderMenuItem(item, isActive, true)}
              </div>
            );
          })}

          {user && (
            <div className="relative group mt-4">
              <img
                src={
                  user.photoURL ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    user.displayName || user.email || 'User'
                  )}&background=6366f1&color=fff`
                }
                alt={user.displayName || user.email || 'User'}
                className="w-8 h-8 rounded-full cursor-pointer"
                title={user.displayName || user.email || 'User'}
              />

              <button
                onClick={onToggleCollapse}
                className="absolute -top-1 -right-1 w-4 h-4 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
                title="Expand sidebar"
              >
                <i className="ri-arrow-right-s-line text-[8px]"></i>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="shrink-0 bg-white w-full border-t border-gray-200 p-3">
          {bottomMenuItems.map((item) => {
            const isActive = currentPath === item.router;
            return renderMenuItem(item, isActive, false, 'mb-2');
          })}

          {user && (
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 mt-1">
              <img
                src={
                  user.photoURL ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    user.displayName || user.email || 'User'
                  )}&background=6366f1&color=fff`
                }
                alt={user.displayName || user.email || 'User'}
                className="w-8 h-8 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-sans font-medium text-gray-900 truncate">
                  {user.displayName || user.firstName || 'User'}
                </p>
                <p className="text-xs font-sans text-gray-500 truncate">
                  {user.email}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-1 rounded-lg hover:bg-red-100 hover:text-red-600 transition-colors group"
                title="Logout"
              >
                <i className="ri-logout-box-line text-[16px] text-gray-400 group-hover:text-red-600"></i>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
